import React, { useEffect } from 'react';
import { tx, localizeDeep, LANGUAGES } from '../utils/i18n';
import { useNavigate } from 'react-router-dom';
import { routePath } from '../i18n/routes';
import { useLanguage } from '../contexts/LanguageContext';
import { FileText, Download, ArrowRight, CheckCircle2, Globe } from 'lucide-react';

const OnePager = () => {
  const navigate = useNavigate();
  const { currentLanguage, switchLanguage } = useLanguage();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handlePrint = () => {
    window.print();
  };

  const handleLanguageChange = (lang) => {
    navigate(routePath('onePager', lang));
  };

  const content = {
    fr: /* i18n:extract */ {
      title: "OptimHub - Plateforme DSP Content Commerce",
      subtitle: "Traffic Brokers : Nous achetons au CPC, vous payez au CPA",
      tagline: "Technologie Deep Link & Solutions Programmatiques Haute Performance",
      aboutTitle: "Qui sommes-nous ?",
      about: "OptimHub est une plateforme DSP leader spécialisée dans le Content Commerce et la technologie Deep Link. Nous connectons les marchands avec des consommateurs hautement engagés via des placements natifs et programmatiques.",
      modelTitle: "Notre Modèle Unique",
      modelDesc: "En tant que Traffic Brokers, nous achetons le trafic au CPC et vous ne payez qu'au CPA (coût par action/vente). Zero risque, ROI maximum.",
      servicesTitle: "Nos 6 Solutions DSP",
      services: [
        {
          name: "Content Commerce",
          desc: "Intégration native dans blogs, news, newsletters. 150M+ lecteurs/mois, 92% taux de confiance.",
          metrics: "ROI 4.8x vs display"
        },
        {
          name: "Deep Link Technology",
          desc: "Technologie propriétaire avec AI Bidder. Targeting contextuel avancé, attribution multi-touch.",
          metrics: "95% précision, ROI 8x"
        },
        {
          name: "MSN Microsoft Network",
          desc: "Placements premium sur Outlook, Bing, Edge. Environnement brand-safe, audiences qualifiées.",
          metrics: "400M+ utilisateurs"
        },
        {
          name: "BNPL Advertising",
          desc: "Publicité sur Klarna, Afterpay, ZIP. Audiences premium à fort pouvoir d'achat.",
          metrics: "AOV 3x supérieur"
        },
        {
          name: "Coupon & Deals",
          desc: "Sites de coupons premium. Audiences haute intention d'achat, conversions rapides.",
          metrics: "> 5% conversion, ROI 3.2x"
        },
        {
          name: "Browser Autocomplete",
          desc: "Capture d'intent en temps réel. Suggestions dans l'autocomplétion navigateur.",
          metrics: "75% engagement, ROI 12x"
        }
      ],
      benefitsTitle: "Pourquoi OptimHub ?",
      benefits: [
        "Modèle CPC vers CPA : Vous payez uniquement pour les conversions réelles",
        "Expertise Traffic Brokers : Optimisation continue de vos campagnes",
        "Technologie Propriétaire : Deep Link Bidder avec IA avancée",
        "Network Premium : Accès à de nombreuses plateformes de monétisation via une API unique",
        "Performance Garantie : ROI 3-12x selon les verticales",
        "Support Multilingue : FR, EN, DE, IT, ES, ZH - Marchés EU, US & Asie"
      ],
      conversionTitle: "Performance",
      conversionRate: "Taux de conversion > 5%",
      partnersTitle: "Plus de 5000 marques nous font confiance",
      partners: "Backmarket, Cirkul, LatestBedding, Prohealth, Dreams, AttractionTickets, myrevair, Macy's, Kohl's, Red by SFR",
      contactTitle: "Contact",
      contact: {
        email: "contact@optimhub.com",
        web: "www.optimhub.com"
      }
    } /* i18n:end */,
    en: {
      title: "OptimHub - Content Commerce DSP Platform",
      subtitle: "Traffic Brokers: We buy at CPC, you pay at CPA",
      tagline: "Deep Link Technology & High-Performance Programmatic Solutions",
      aboutTitle: "Who We Are",
      about: "OptimHub is a leading DSP platform specialized in Content Commerce and Deep Link Technology. We connect merchants with highly engaged consumers through native and programmatic placements.",
      modelTitle: "Our Unique Model",
      modelDesc: "As Traffic Brokers, we buy traffic at CPC and you only pay at CPA (cost per action/sale). Zero risk, maximum ROI.",
      servicesTitle: "Our 6 DSP Solutions",
      services: [
        {
          name: "Content Commerce",
          desc: "Native integration in blogs, news, newsletters. 150M+ monthly readers, 92% trust rate.",
          metrics: "ROI 4.8x vs display"
        },
        {
          name: "Deep Link Technology",
          desc: "Proprietary technology with AI Bidder. Advanced contextual targeting, multi-touch attribution.",
          metrics: "95% precision, 8x ROI"
        },
        {
          name: "MSN Microsoft Network",
          desc: "Premium placements on Outlook, Bing, Edge. Brand-safe environment, qualified audiences.",
          metrics: "400M+ users"
        },
        {
          name: "BNPL Advertising",
          desc: "Advertising on Klarna, Afterpay, ZIP. Premium audiences with high purchasing power.",
          metrics: "3x higher AOV"
        },
        {
          name: "Coupon & Deals",
          desc: "Premium coupon sites. High purchase intent audiences, fast conversions.",
          metrics: "> 5% conversion, 3.2x ROI"
        },
        {
          name: "Browser Autocomplete",
          desc: "Real-time intent capture. Suggestions in browser autocomplete.",
          metrics: "75% engagement, 12x ROI"
        }
      ],
      benefitsTitle: "Why OptimHub?",
      benefits: [
        "CPC to CPA Model: You only pay for real conversions",
        "Traffic Broker Expertise: Continuous campaign optimization",
        "Proprietary Technology: Deep Link Bidder with advanced AI",
        "Premium Network: Access to many monetization platforms through a single API",
        "Guaranteed Performance: 3-12x ROI depending on verticals",
        "Multilingual Support: FR, EN, DE, IT, ES, ZH - EU, US & Asia markets"
      ],
      conversionTitle: "Performance",
      conversionRate: "Conversion rate > 5%",
      partnersTitle: "Over 5000 brands trust us",
      partners: "Backmarket, Cirkul, LatestBedding, Prohealth, Dreams, AttractionTickets, myrevair, Macy's, Kohl's, Red by SFR",
      contactTitle: "Contact",
      contact: {
        email: "contact@optimhub.com",
        web: "www.optimhub.com"
      }
    },
    de: {
      title: "OptimHub - Content Commerce DSP-Plattform",
      subtitle: "Traffic Brokers: Wir kaufen zu CPC, Sie zahlen zu CPA",
      tagline: "Deep Link Technologie & Hochleistungs-Programmatic-Lösungen",
      aboutTitle: "Wer wir sind",
      about: "OptimHub ist eine führende DSP-Plattform, spezialisiert auf Content Commerce und Deep Link-Technologie. Wir verbinden Händler mit hochengagierten Verbrauchern durch native und programmatische Platzierungen.",
      modelTitle: "Unser einzigartiges Modell",
      modelDesc: "Als Traffic Broker kaufen wir Traffic zu CPC und Sie zahlen nur zu CPA (Kosten pro Aktion/Verkauf). Null Risiko, maximaler ROI.",
      servicesTitle: "Unsere 6 DSP-Lösungen",
      services: [
        {
          name: "Content Commerce",
          desc: "Native Integration in Blogs, News, Newsletter. 150M+ monatliche Leser, 92% Vertrauensrate.",
          metrics: "ROI 4.8x vs Display"
        },
        {
          name: "Deep Link Technologie",
          desc: "Proprietäre Technologie mit AI Bidder. Fortgeschrittenes kontextuelles Targeting, Multi-Touch-Attribution.",
          metrics: "95% Präzision, 8x ROI"
        },
        {
          name: "MSN Microsoft Network",
          desc: "Premium-Platzierungen auf Outlook, Bing, Edge. Markensichere Umgebung, qualifizierte Zielgruppen.",
          metrics: "400M+ Nutzer"
        },
        {
          name: "BNPL Werbung",
          desc: "Werbung auf Klarna, Afterpay, ZIP. Premium-Zielgruppen mit hoher Kaufkraft.",
          metrics: "3x höherer AOV"
        },
        {
          name: "Coupon & Deals",
          desc: "Premium-Coupon-Sites. Zielgruppen mit hoher Kaufabsicht, schnelle Conversions.",
          metrics: "> 5% Conversion, 3.2x ROI"
        },
        {
          name: "Browser Autocomplete",
          desc: "Echtzeit-Intent-Erfassung. Vorschläge in Browser-Autovervollständigung.",
          metrics: "75% Engagement, 12x ROI"
        }
      ],
      benefitsTitle: "Warum OptimHub?",
      benefits: [
        "CPC zu CPA Modell: Sie zahlen nur für echte Conversions",
        "Traffic Broker Expertise: Kontinuierliche Kampagnenoptimierung",
        "Proprietäre Technologie: Deep Link Bidder mit fortgeschrittener KI",
        "Premium-Netzwerk: Zugang zu vielen Monetarisierungsplattformen über eine einzige API",
        "Garantierte Leistung: 3-12x ROI je nach Vertikal",
        "Mehrsprachiger Support: FR, EN, DE, IT, ES, ZH - EU-, US- & Asien-Märkte"
      ],
      conversionTitle: "Leistung",
      conversionRate: "Conversion-Rate > 5%",
      partnersTitle: "Über 5000 Marken vertrauen uns",
      partners: "Backmarket, Cirkul, LatestBedding, Prohealth, Dreams, AttractionTickets, myrevair, Macy's, Kohl's, Red by SFR",
      contactTitle: "Kontakt",
      contact: {
        email: "contact@optimhub.com",
        web: "www.optimhub.com"
      }
    }
  };

  const t = content[currentLanguage] || localizeDeep(content.fr, currentLanguage);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top Bar with Buttons - Hidden in print */}
      <div className="print:hidden fixed top-4 right-4 z-50 flex items-center gap-3">
        {/* Language Switcher */}
        <div className="bg-white rounded-lg shadow-lg border border-gray-200 flex items-center overflow-hidden">
          {LANGUAGES.map((language) => (
            <button
              key={language.code}
              data-testid={`one-pager-lang-${language.code}`}
              onClick={() => handleLanguageChange(language.code)}
              className={`px-3 py-2 text-sm font-semibold transition-colors duration-200 ${
                currentLanguage === language.code
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              {language.code === 'zh' ? '中文' : language.code.toUpperCase()}
            </button>
          ))}
        </div>
        
        {/* Download PDF Button */}
        <button
          onClick={handlePrint}
          className="bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors duration-200 flex items-center gap-2 shadow-lg"
        >
          <Download className="w-5 h-5" />
          {tx(currentLanguage, 'Télécharger PDF', 'Download PDF', 'PDF herunterladen')}
        </button>
      </div>

      {/* Back Button - Hidden in print */}
      <div className="print:hidden fixed top-4 left-4 z-50">
        <button
          onClick={() => window.location.href = routePath('home', currentLanguage)}
          className="bg-white text-gray-700 px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors duration-200 flex items-center gap-2 shadow-lg border border-gray-200"
        >
          <ArrowRight className="w-5 h-5 rotate-180" />
          {tx(currentLanguage, 'Retour', 'Back', 'Zurück')}
        </button>
      </div>

      {/* One-Pager Content - Optimized for A4 Print */}
      <div className="max-w-[210mm] mx-auto bg-white shadow-2xl print:shadow-none" style={{ minHeight: '297mm' }}>
        <div className="p-8 print:p-12">
          {/* Header */}
          <div className="text-center mb-6">
            <div className="flex justify-center items-center mb-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_traffic-partner/artifacts/hrx2swuq_logo2%20%281%29.png"
                alt="OptimHub Logo"
                className="h-12 print:h-10"
              />
            </div>
            <h1 className="text-3xl print:text-2xl font-bold text-gray-900 mb-1">{t.title}</h1>
            <p className="text-lg print:text-base font-semibold text-blue-600 mb-1">{t.subtitle}</p>
            <p className="text-sm print:text-xs text-gray-600">{t.tagline}</p>
          </div>

          {/* About Section */}
          <div className="mb-4 print:mb-3">
            <h2 className="text-xl print:text-lg font-bold text-gray-900 mb-2 pb-1 border-b-2 border-blue-600">{t.aboutTitle}</h2>
            <p className="text-sm print:text-xs text-gray-700 leading-relaxed">{t.about}</p>
          </div>

          {/* Unique Model */}
          <div className="bg-gradient-to-r from-blue-50 to-cyan-50 print:bg-gray-50 p-4 print:p-3 rounded-lg mb-4 print:mb-3">
            <h3 className="text-lg print:text-base font-bold text-blue-900 mb-1">{t.modelTitle}</h3>
            <p className="text-sm print:text-xs text-gray-700">{t.modelDesc}</p>
          </div>

          {/* Services Grid */}
          <div className="mb-4 print:mb-3">
            <h2 className="text-xl print:text-lg font-bold text-gray-900 mb-3 print:mb-2 pb-1 border-b-2 border-blue-600">{t.servicesTitle}</h2>
            <div className="grid grid-cols-2 gap-3 print:gap-2">
              {t.services.map((service, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-3 print:p-2 bg-white">
                  <h3 className="text-sm print:text-xs font-bold text-gray-900 mb-1">{service.name}</h3>
                  <p className="text-xs print:text-[10px] text-gray-600 mb-1 leading-tight">{service.desc}</p>
                  <div className="text-xs print:text-[10px] font-semibold text-blue-600">{service.metrics}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Performance Badge */}
          <div className="mb-4 print:mb-3">
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 print:bg-gray-50 p-4 print:p-3 rounded-lg border-2 border-green-500 text-center">
              <h3 className="text-lg print:text-base font-bold text-green-900 mb-1">{t.conversionTitle}</h3>
              <div className="text-2xl print:text-xl font-bold text-green-600">{t.conversionRate}</div>
            </div>
          </div>

          {/* Benefits */}
          <div className="mb-4 print:mb-3">
            <h2 className="text-xl print:text-lg font-bold text-gray-900 mb-2 print:mb-1 pb-1 border-b-2 border-blue-600">{t.benefitsTitle}</h2>
            <div className="grid grid-cols-2 gap-2 print:gap-1">
              {t.benefits.map((benefit, index) => (
                <div key={index} className="flex items-start gap-2 print:gap-1">
                  <CheckCircle2 className="w-4 h-4 print:w-3 print:h-3 text-blue-600 flex-shrink-0 mt-0.5" />
                  <span className="text-xs print:text-[10px] text-gray-700 leading-tight">{benefit}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Partners */}
          <div className="mb-4 print:mb-3">
            <h2 className="text-xl print:text-lg font-bold text-gray-900 mb-2 print:mb-1 pb-1 border-b-2 border-blue-600">{t.partnersTitle}</h2>
            <p className="text-xs print:text-[10px] text-gray-700 italic">{t.partners}</p>
          </div>

          {/* Contact Footer */}
          <div className="bg-gradient-to-r from-blue-600 to-cyan-600 text-white p-3 print:p-2 rounded-lg">
            <h2 className="text-lg print:text-base font-bold mb-2 print:mb-1">{t.contactTitle}</h2>
            <div className="grid grid-cols-2 gap-4 print:gap-3 text-sm print:text-xs">
              <div>
                <div className="font-semibold mb-1">Email</div>
                <div>{t.contact.email}</div>
              </div>
              <div>
                <div className="font-semibold mb-1">Web</div>
                <div>{t.contact.web}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Print Styles */}
      <style jsx>{`
        @media print {
          @page {
            size: A4;
            margin: 0;
          }
          
          body {
            print-color-adjust: exact;
            -webkit-print-color-adjust: exact;
          }
          
          * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
        }
      `}</style>
    </div>
  );
};

export default OnePager;
